/**
 * StepList — Vertikale Step-Liste mit Drag-and-Drop + Status-Indikator.
 * @since v2.514.0 (#1173)
 *
 * Props:
 *   - steps: DSL-Step-Array
 *   - selectedId: id des aktiven Steps
 *   - statuses: {[stepId]: 'ok'|'warning'|'error'|'untested'}
 *   - onSelect(id), onReorder(newSteps), onAdd(), onDelete(idx), onDuplicate(idx)
 */
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import StepStatusBadge from './StepStatusBadge';

export default function StepList({ steps = [], selectedId, statuses = {}, onSelect, onReorder, onAdd, onDelete, onDuplicate }) {
  function handleDragEnd(res) {
    if (!res.destination) return;
    const next = [...steps];
    const [moved] = next.splice(res.source.index, 1);
    next.splice(res.destination.index, 0, moved);
    onReorder && onReorder(next);
  }

  function describeKind(step) {
    if (step.skill_id) return `Skill #${step.skill_id}${step.skill_slug ? ` · ${step.skill_slug}` : ''}`;
    if (step.type === 'builtin') return `builtin · ${step.action}`;
    if (step.type === 'action') return `action · ${step.action_type}`;
    if (step.type) return step.type;
    return 'unbekannt';
  }

  return (
    <div className="space-y-2">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="steps">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1.5">
              {steps.map((step, idx) => {
                const isSelected = step.id === selectedId;
                return (
                  <Draggable key={step.id || `idx-${idx}`} draggableId={step.id || `idx-${idx}`} index={idx}>
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        onClick={() => onSelect && onSelect(step.id)}
                        className={`relative group cursor-pointer rounded-lg border p-2.5 transition-colors ${
                          isSelected
                            ? 'border-[#338edc] bg-[#338edc]/5 ring-1 ring-[#338edc]/30'
                            : 'border-[var(--border-subtle,#E5E7EB)] bg-white hover:border-[#338edc]/40'
                        } ${snap.isDragging ? 'shadow-lg' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <div {...prov.dragHandleProps} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] cursor-grab active:cursor-grabbing">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-[var(--text-tertiary)] flex-shrink-0">#{idx + 1}</span>
                              <span className="text-xs font-semibold text-[var(--text-primary)] truncate">{step.id || '<ohne id>'}</span>
                              <StepStatusBadge status={statuses[step.id] || 'untested'} />
                            </div>
                            <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5 font-mono truncate">{describeKind(step)}</div>
                            {step.condition && (
                              <div className="text-[10px] text-amber-700 mt-0.5 font-mono truncate" title={step.condition}>
                                if: {step.condition}
                              </div>
                            )}
                            {step.early_return_if && (
                              <div className="text-[10px] text-rose-700 mt-0.5 font-mono truncate" title={step.early_return_if}>
                                early-return-if: {step.early_return_if}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onDuplicate && onDuplicate(idx); }}
                              title="Duplizieren"
                              className="p-1 text-[var(--text-tertiary)] hover:text-[#338edc]"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); if (confirm(`Step '${step.id}' wirklich löschen?`)) onDelete && onDelete(idx); }}
                              title="Löschen"
                              className="p-1 text-[var(--text-tertiary)] hover:text-rose-600"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <button
        type="button"
        onClick={onAdd}
        className="w-full px-3 py-2 text-xs border border-dashed border-[var(--border-subtle,#E5E7EB)] rounded-lg text-[var(--text-secondary)] hover:border-[#338edc] hover:text-[#338edc] transition-colors"
      >
        + Step hinzufügen
      </button>
      {steps.length === 0 && (
        <div className="text-center text-xs text-[var(--text-tertiary)] py-6 italic">Noch keine Steps — auf „+ Step hinzufügen" klicken.</div>
      )}
    </div>
  );
}
